import { shouldNavigate } from '../utils/navigation';
import { albumQualityLabels, describeAlbumResolution } from '../domain/library';
import { albumPath, entityPath, slugify, type EntityType } from '../domain/routes';
import type { AlbumSummary } from '../types';
import { CoverArt } from './CoverArt';

interface AlbumCardProps {
  album: AlbumSummary;
  onNavigate?: (path: string) => void;
  onEntityClick?: (type: EntityType, value: string) => void;
  reason?: string;
  eager?: boolean;
  highlightGenres?: string[];
}

export function AlbumCard({ album, onNavigate, onEntityClick, reason, eager, highlightGenres = [] }: AlbumCardProps) {
  const quality = albumQualityLabels(album).filter((label) => label !== 'FLAC').slice(0, 1);
  // When filtering by genre, show the genre that matched rather than the album's first one.
  const selected = new Set(highlightGenres.map(slugify));
  const genre = album.genres.find((value) => selected.has(slugify(value))) ?? album.genres[0];
  return (
    <article className="album-card">
      <a className="album-card__main" href={albumPath(album)} aria-label={`View ${album.artist} — ${album.title}`} onClick={(event) => { if (onNavigate) { if (!shouldNavigate(event)) return; event.preventDefault(); onNavigate(albumPath(album)); } }}>
        <span className="visually-hidden">View album</span>
      </a>
      <div className="cover-wrap"><CoverArt src={album.cover} artist={album.artist} title={album.title} eager={eager} /></div>
      <div className="card-copy">
        <a className="card-artist" href={entityPath('artist', album.artist)} onClick={(event) => { if (onEntityClick) { if (!shouldNavigate(event)) return; event.preventDefault(); onEntityClick('artist', album.artist); } }}>{album.artist}</a>
        <h2>{album.title}</h2>
        <p>{album.year ?? 'Year unknown'} · {describeAlbumResolution(album)}</p>
        <div className="card-tags">
          {genre && [genre].map((genre) => <a key={genre} href={entityPath('genre', genre)} onClick={(event) => { if (onEntityClick) { if (!shouldNavigate(event)) return; event.preventDefault(); onEntityClick('genre', genre); } }}>{genre}</a>)}
          {quality.map((label) => <a key={label} href={entityPath('quality', label)} onClick={(event) => { if (onEntityClick) { if (!shouldNavigate(event)) return; event.preventDefault(); onEntityClick('quality', label); } }}>{label}</a>)}
        </div>
        {reason && <span className="match-reason">Matched {reason}</span>}
      </div>
    </article>
  );
}
